import {
  DATASET_PARAM,
  DATASET_STORAGE_KEY,
  formatDatasetSelection,
  LIVE_ACCOUNT_PARAM,
  type DatasetSelection,
} from './config';

// T-283 — moving between accounts: the remembered choice, the address a choice becomes, and the one place the
// app navigates to it.
//
// **Why a navigation and not a state change.** The app has exactly ONE `ApiClient`, built once in `main.tsx`
// before React starts (ARCH §8.2, §9.1) — the demo server, its storage namespace, the clock and the whole
// query cache hang off it. Swapping the dataset under a running app would mean rebuilding all of that and
// invalidating every cached response, for an action a founder takes once a session; a navigation rebuilds it
// honestly and costs one load of a static build. So choosing an account sets the address and reloads, and the
// address is the truth about what is on screen — which is also what makes the switch's entries linkable.
//
// The remembered choice is `localStorage`, deliberately OUTSIDE the demo namespace (`sl.demo.…`), because it
// says which account to open and must survive "Reset demo" — the same reasoning D-097 (7) applies to the
// tree's expansion and T-044 to the recently-opened list.

/** The remembered choice, or `null` when there is none (or storage is refused). */
export function readStoredDataset(): string | null {
  try {
    return window.localStorage.getItem(DATASET_STORAGE_KEY);
  } catch {
    return null; // private mode: the app opens on the demo, as it always did
  }
}

export function storeDataset(selection: DatasetSelection): void {
  try {
    window.localStorage.setItem(DATASET_STORAGE_KEY, formatDatasetSelection(selection));
  } catch {
    /* quota or private mode — the choice simply is not remembered */
  }
}

/**
 * The address a choice becomes: `?dataset=…` (and `?account=` for a named live entry) in the SEARCH part, with
 * the hash route left exactly as it is, so switching accounts keeps you on the screen you were reading.
 *
 * Every other query parameter is dropped on purpose. The five QA switches belong to a demo build and a live
 * account honours none of them (`readAppConfig`), so carrying `?demoFault=error` across the switch would arm a
 * fault the new account cannot answer; and a stale `?account=` must not outlive a move back to the demo.
 */
export function datasetHref(
  location: Pick<Location, 'origin' | 'pathname' | 'hash'>,
  selection: DatasetSelection,
): string {
  const params = new URLSearchParams();
  if (selection.dataset === 'live') {
    params.set(DATASET_PARAM, 'live');
    if (selection.liveAccountKey !== null) params.set(LIVE_ACCOUNT_PARAM, selection.liveAccountKey);
  }
  const search = params.toString();
  return `${location.origin}${location.pathname}${search ? `?${search}` : ''}${location.hash}`;
}

/**
 * Go to an account: remember it, then load its address.
 *
 * `location.assign` lives behind this seam for the same reason `reloadPage` does (`app/reload.ts`): jsdom's
 * `Location` is not configurable, so a test that cannot watch the navigation cannot prove the switch went
 * where it says it went.
 */
export function goToDataset(selection: DatasetSelection): void {
  storeDataset(selection);
  window.location.assign(datasetHref(window.location, selection));
}
